import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentAPI } from '../utils/api';
import { CheckCircle, XCircle } from '@phosphor-icons/react';

/**
 * Stripe return page. We poll `paymentAPI.checkStatus` until Stripe confirms
 * the session is paid, then render an itemized receipt — every line item the
 * rider was charged, so there's no ambiguity about what they paid for.
 */
const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [breakdown, setBreakdown] = useState(null);

  useEffect(() => {
    // Square appends ?orderId=...&transactionId=... to the redirect URL.
    // Stripe used {CHECKOUT_SESSION_ID} substitution into ?session_id=... .
    // Accept both so legacy Stripe links still resolve.
    const sessionId = searchParams.get('session_id') || searchParams.get('orderId');
    if (sessionId) {
      checkStatus(sessionId);
    } else {
      setStatus('success');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkStatus = async (sessionId, attempts = 0) => {
    try {
      const { data } = await paymentAPI.checkStatus(sessionId);
      if (data.breakdown) setBreakdown(data.breakdown);
      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (attempts < 5) {
        setStatus('pending');
        setTimeout(() => checkStatus(sessionId, attempts + 1), 2000);
      } else {
        setStatus('pending');
      }
    } catch {
      setStatus('success');
    }
  };

  const renderBreakdown = () => {
    if (!breakdown) return null;
    const rows = [
      { label: 'Class entries', value: breakdown.entry_fees, testid: 'success-entry-fees' },
      { label: 'Stalls / RV hookups', value: breakdown.accommodation_fees, testid: 'success-accommodation-fees' },
      { label: 'Show merch / add-ons', value: breakdown.addon_fees, testid: 'success-addon-fees' },
      { label: 'Office/Rider Fee', value: breakdown.processing_fee, testid: 'success-processing-fee' },
      { label: 'Service fee', value: breakdown.service_fee, testid: 'success-service-fee' },
    ].filter(r => Number(r.value) > 0);
    if (!rows.length && !breakdown.grand_total) return null;
    return (
      <div className="mt-8 mx-auto max-w-md text-left bg-[#161616] border border-[#262626] rounded-lg p-5" data-testid="payment-success-breakdown">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4" style={{ fontFamily: 'Outfit' }}>
          Receipt
        </h2>
        <div className="space-y-2 mb-4">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between text-sm" data-testid={r.testid}>
              <span className="text-[#9E9E9E]">{r.label}</span>
              <span className="text-white tabular-nums" style={{ fontFamily: 'JetBrains Mono' }}>
                ${Number(r.value).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-[#262626]">
          <span className="text-white font-bold">Total Paid</span>
          <span className="text-[#34C759] font-bold text-lg tabular-nums" style={{ fontFamily: 'JetBrains Mono' }} data-testid="success-grand-total">
            ${Number(breakdown.grand_total || 0).toFixed(2)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-12">
      <div className="text-center max-w-2xl w-full">
        {status === 'loading' ? (
          <div className="shimmer h-16 w-16 rounded-full mx-auto mb-6"></div>
        ) : status === 'success' ? (
          <>
            <div className="w-20 h-20 bg-[#34C759]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} weight="fill" className="text-[#34C759]" />
            </div>
            <h1 className="text-4xl font-black text-white mb-3" style={{ fontFamily: 'Outfit' }} data-testid="payment-success-title">Payment Successful!</h1>
            <p className="text-[#9E9E9E] mb-2">Your entry has been confirmed and payment received.</p>
            {renderBreakdown()}
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-[#FACC15]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} weight="fill" className="text-[#FACC15]" />
            </div>
            <h1 className="text-4xl font-black text-white mb-3" style={{ fontFamily: 'Outfit' }}>Payment Pending</h1>
            <p className="text-[#9E9E9E] mb-2">Your entry is submitted but payment is still processing.</p>
            {renderBreakdown()}
          </>
        )}
        <div className="flex flex-col gap-3 mt-8 max-w-md mx-auto">
          {status === 'success' && (searchParams.get('session_id') || searchParams.get('orderId')) && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/receipt/${searchParams.get('session_id') || searchParams.get('orderId')}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                // Both Stripe (session_id) and Square (orderId) callers land here.
                // We always download via fetch+Bearer so the auth-gated PDF route
                // sees the token.
                e.preventDefault();
                (async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const sessionId = searchParams.get('session_id') || searchParams.get('orderId');
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/receipt/${sessionId}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `showmaster-receipt-${sessionId.slice(-8)}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Receipt download failed', err);
                  }
                })();
              }}
              className="px-6 py-3 bg-[#34C759] hover:bg-[#34C759]/80 text-white font-semibold rounded-md transition-colors text-center"
              data-testid="download-receipt-btn"
            >
              Download Receipt (PDF)
            </a>
          )}
          <button
            onClick={() => navigate('/shows')}
            className="px-6 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors"
            data-testid="browse-shows-btn"
          >
            Browse More Shows
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-transparent border border-[#262626] text-[#9E9E9E] rounded-md hover:text-white hover:border-[#0096FF] transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
